import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Grid from "../components/Grid"; // Import the Grid component
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Units({ onAddUnitClick }) {
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState({ field: "unit_name", direction: "asc" });
  const [selectedSearchField, setSelectedSearchField] = useState("unit_name"); // New state for search field

  useEffect(() => {
    // Fetch units from the API when the component mounts
    const fetchUnits = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/units`);
        // Map the units to include an id field
        const unitsWithId = response.data.units.map((unit) => ({
          ...unit,
          id: unit.unit_code, // Assign _id to id
        }));
        setUnits(unitsWithId); // // Set units with id field
      } catch (error) {
        console.error("Error fetching units:", error);
        toast.error("Error fetching units");
      }
    };

    fetchUnits();
  }, []);

  const handleUnitClick = (unit) => {
    setSelectedUnit({
      ...unit,
      status: unit.status || "Active",
    });
  };

  const handleClosePopup = () => {
    setSelectedUnit(null);
  };

  const handleUpdateUnit = async () => {
    if (!selectedUnit) return;
    try {
      const response =  await axios.put(
        `${API_BASE_URL}/units/update/${selectedUnit.unit_code}`,
        { status: selectedUnit.status }
      );
      toast.success(response.data.message);
      setSelectedUnit(null);

      // Refresh categories after update
      const refreshedUnits = await axios.get(`${API_BASE_URL}/units`);
      setUnits(refreshedUnits.data.units);
    } catch (error) {
      console.error("Error updating unit status:", error);
      toast.error("Error updating unit status");
    }
  };

  // Filter and sort the production batches
  const filteredUnits = units
    .filter((unit) => {
      switch (selectedSearchField) {
        case "unit_code":
          return (
            unit.unit_code &&
            unit.unit_code.toLowerCase().includes(searchTerm.toLowerCase())
          );
        case "unit_name":
          return (
            unit.unit_name &&
            unit.unit_name.toLowerCase().includes(searchTerm.toLowerCase())
          );
        case "status":
          return (
            unit.status &&
            unit.status.toLowerCase().includes(searchTerm.toLowerCase())  
          );
        default:
          return false;
      }
    })
    .sort((a, b) => {
      if (a[sort.field] < b[sort.field])
        return sort.direction === "asc" ? -1 : 1;
      if (a[sort.field] > b[sort.field])
        return sort.direction === "asc" ? 1 : -1;
      return 0;
    });

    const handleDownloadExcel = async () => {
    const dataToExport = searchTerm.trim().length > 0 ? filteredUnits : units;

    if (dataToExport.length === 0) {
      toast.warn("No unit data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Units");

    const headerRow = ["Unit Code", "Unit Name", "Status"];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((unit) => {
      worksheet.addRow([
        unit.unit_code,
        unit.unit_name,
        unit.status || "N/A",
      ]);
    });

    worksheet.columns.forEach((column, index) => {
      const headerText = headerRow[index];
      const maxLength = Math.max(
        headerText.length,
        ...dataToExport.map((row) =>
          row[headerText.toLowerCase().replace(" ", "_")]?.toString().length || 10
        )
      );
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "units.xlsx");
  };

  const columns = [
    { field: "unit_code", headerName: "ID", width: 90, flex: 1 }, // Use id here
    { field: "unit_name", headerName: "Unit Name", width: 150, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-[#388E3C] text-white"
            : "bg-red-500 text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
            onClick={() => handleUnitClick(params.row)}
            className="bg-green-700 text-white px-2 py-1 flex items-center rounded-lg"
          >
            <FaEdit className="text-white mr-2" />
            Edit
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Units</h1>
          <button
            onClick={onAddUnitClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add Unit
          </button>
        </div>

        {/* Unit List */}
        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="unit_name">Unit Name</option>
                <option value="unit_code">Unit Code</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Batch here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          {/* Product Grid */}
          <Grid rows={filteredUnits} columns={columns} getRowId={(row) => row.unit_code}/>
        </div>

        {/* Popup for Units Details */}
        {selectedUnit && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedUnit.unit_code}
                  </h2>
                  <div className="mb-4">
                    <label className="block text-gray-700">Unit Name</label>
                    <input
                      type="text"
                      value={selectedUnit.unit_name}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      readOnly
                    />
                  </div>
                   {/* Status Dropdown */}
                   <div className="mb-4">
                    <label className="block text-gray-700">Status</label>
                    <select
                      value={selectedUnit.status}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedUnit({
                          ...selectedUnit,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <button
                    onClick={handleUpdateUnit}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Units;
